import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import WidgetPageContainerAnimation from '../components/WidgetPage/WidgetStyle.style';
import { NavigationVariants, ANIMATIONSTATES as ANIMATIONS } from './animations';

export function isWidgetURL(url, widgets) {
  return widgets.find((nav) => url.startsWith(nav.linkTo.pathname));
}

export function isHomepageURL(url, linkUrlFor) {
  return url === linkUrlFor('/') || url === linkUrlFor('');
}

export function resetWidgetTransformValue(el) {
  if (el) {
    const currentTransformValue = getComputedStyle(el).transform;
    setTimeout(() => {
      if (currentTransformValue !== 'none') {
        el.style.transform = 'none';
      }
    }, 50);
  }
}

export function withRouter(Component) {
  function ComponentWithRouterProp(props) {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    return <Component {...props} location={location} router={{ navigate, params }} />;
  }
  return ComponentWithRouterProp;
}

export function WidgetPageWrapperWithRouteProps(props) {
  let initial = 'invisible';
  let animate = 'visible';
  const { animation, navigationHistory, location, timeoutResetTranform } = props;
  const forExitingWidget =
    navigationHistory.getPreviousWidget() ===
    navigationHistory.getAppForPath(props.linkUrlFor(props.widgetPath));
  if (animation === ANIMATIONS.betweenWidgets) {
    /* Going to a widget */
    if (navigationHistory.isLatestActionPushUrl()) {
      if (forExitingWidget) {
        /* Animation variants for the old widget */
        initial = 'visible';
      } else {
        /* Animation variants for the new widget */
        initial = 'moveOutRight';
        animate = 'moveIn';
      }
    } else {
      /* Navigating back to a widget */
      if (forExitingWidget) {
        /* Animation variants for the old widget */
        initial = 'visible';
        animate = 'moveIn';
      } else {
        /* Animation variants for the new widget */
        initial = 'moveOutLeft';
        animate = 'moveIn';
      }
    }
  } else if (animation === ANIMATIONS.fromHome) {
    initial = 'moveOutRight';
    animate = 'moveIn';
  } else if (animation === ANIMATIONS.toHome) {
    initial = 'moveOutRight';
    animate = 'moveOutRight';
  } else if (animation === ANIMATIONS.toPopover || animation === ANIMATIONS.inWidget) {
    initial = 'moveIn';
    animate = 'moveIn';
  }

  const exitFn = (customExitProperty) => {
    /* This can be undefined (due to rerenderings?). Should be checked. */
    if (customExitProperty !== undefined) {
      const { currentURL, previousURL } = customExitProperty;
      if (navigationHistory.isHomepageURL(currentURL))
        return NavigationVariants.moveOutRight;
      if (navigationHistory.isPopoverURL(currentURL)) return NavigationVariants.invisible;
      if (navigationHistory.isWidgetURL(currentURL)) {
        if (navigationHistory.isNavigatingInSameWidget())
          return NavigationVariants.invisible;
        if (navigationHistory.isLatestActionPushUrl())
          return NavigationVariants.moveOutLeft;
        return NavigationVariants.moveOutRight;
      }
      return NavigationVariants.moveOutLeft;
    }
    return NavigationVariants.invisible;
  };

  /* After the animation we reset the transform value to 'none' so it doesn't mess with the zIndex stack.
   When the initial & animate value are the same the onAnimationComplete does not get executed */
  if (timeoutResetTranform) {
    clearTimeout(timeoutResetTranform);
  }

  if (initial === animate) {
    props.setTimeoutResetTranform(
      setTimeout(() => {
        if (props.widgetAnimationWrapper && props.widgetAnimationWrapper.current) {
          resetWidgetTransformValue(props.widgetAnimationWrapperRef.current);
        }
        clearTimeout(timeoutResetTranform);
      }, 500)
    );
  }
  return (
    <WidgetPageContainerAnimation
      key={navigationHistory.getAppNameForPath(location.pathname)}
      initial={initial}
      exit={exitFn}
      animate={animate}
      variants={NavigationVariants}
      ref={props.widgetAnimationWrapperRef}
      data-test-widget-page-animation
      /* $ prefix to make the prop transient (https://styled-components.com/docs/api#transient-props)
       * because passing it down to the motion component causes a warning
       */
      $withMaxWidth="true"
    >
      <div id="widgetpage" data-test-page-wrapper tw="h-full flex flex-col">
        {props.children}
      </div>
    </WidgetPageContainerAnimation>
  );
}
